
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as entity from '../entity'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line

/**
 * TokenAccessResult struct
 */
class TokenAccessResult {
  /**
   * Initialize struct
   * @param {!number=} pairId
   * @param {!number=} login
   * @constructor
   */
  constructor (pairId = new UInt64(0, 0), login = new UInt64(0, 0)) {
    this.PairId = pairId
    this.Login = login
  }

  /**
   * Copy struct (shallow copy)
   * @this {!TokenAccessResult}
   * @param {!TokenAccessResult} other Other struct
   * @returns {!TokenAccessResult} This struct
   */
  copy (other) {
    if (other.PairId != null) {
      this.PairId = UInt64.fromNumber(other.PairId)
    } else {
      this.PairId = null
    }
    if (other.Login != null) {
      this.Login = UInt64.fromNumber(other.Login)
    } else {
      this.Login = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!TokenAccessResult}
   * @returns {!TokenAccessResult} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new TokenAccessResultModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new TokenAccessResultModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!TokenAccessResult}
   * @param {!TokenAccessResult} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof TokenAccessResult)) {
      throw new TypeError('Instance of TokenAccessResult is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!TokenAccessResult}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      PairId: ((this.PairId != null) ? this.PairId.toNumber() : null),
      Login: ((this.Login != null) ? this.Login.toNumber() : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return TokenAccessResult.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!TokenAccessResult} other Object value
   * @returns {!TokenAccessResult} Created struct
   */
  static fromObject (other) {
    return new TokenAccessResult().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!TokenAccessResult}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return TokenAccessResult.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!TokenAccessResult}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 14106
  }
}

export { TokenAccessResult };

/**
 * Fast Binary Encoding TokenAccessResult field model
 */
class FieldModelTokenAccessResult extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.PairId = new fbe.FieldModelUInt64(buffer, 4 + 4)
    this.Login = new fbe.FieldModelUInt64(buffer, this.PairId.fbeOffset + this.PairId.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelTokenAccessResult}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelTokenAccessResult}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.PairId.fbeSize + this.Login.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelTokenAccessResult}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.PairId.fbeExtra + this.Login.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelTokenAccessResult}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelTokenAccessResult.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelTokenAccessResult}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 14106
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelTokenAccessResult}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelTokenAccessResult}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.PairId.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.PairId.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.PairId.fbeSize

    if ((fbeCurrentSize + this.Login.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Login.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Login.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelTokenAccessResult}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelTokenAccessResult}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelTokenAccessResult}
   * @param {!TokenAccessResult} fbeValue Default value, defaults is new TokenAccessResult()
   * @returns {!TokenAccessResult} TokenAccessResult value
   */
  get (fbeValue = new TokenAccessResult()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelTokenAccessResult}
   * @param {!TokenAccessResult} fbeValue TokenAccessResult value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.PairId.fbeSize) <= fbeStructSize) {
      fbeValue.PairId = this.PairId.get()
    } else {
      fbeValue.PairId = new UInt64(0, 0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.PairId.fbeSize

    if ((fbeCurrentSize + this.Login.fbeSize) <= fbeStructSize) {
      fbeValue.Login = this.Login.get()
    } else {
      fbeValue.Login = new UInt64(0, 0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Login.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelTokenAccessResult}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelTokenAccessResult}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelTokenAccessResult}
   * @param {!TokenAccessResult} fbeValue TokenAccessResult value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelTokenAccessResult}
   * @param {TokenAccessResult} fbeValue TokenAccessResult value
   */
  setFields (fbeValue) {
    this.PairId.set(fbeValue.PairId)
    this.Login.set(fbeValue.Login)
  }
}

export { FieldModelTokenAccessResult };

/**
 * Fast Binary Encoding TokenAccessResult model
 */
class TokenAccessResultModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelTokenAccessResult(this.buffer, 4)
  }

  /**
   * Get the TokenAccessResult model
   * @this {!TokenAccessResultModel}
   * @returns {!FieldModelTokenAccessResult} model TokenAccessResult model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!TokenAccessResultModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!TokenAccessResultModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return TokenAccessResultModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!TokenAccessResultModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelTokenAccessResult.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!TokenAccessResultModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!TokenAccessResultModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!TokenAccessResultModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!TokenAccessResultModel}
   * @param {!TokenAccessResult} value TokenAccessResult value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!TokenAccessResultModel}
   * @param {!TokenAccessResult} value TokenAccessResult value, defaults is new TokenAccessResult()
   * @return {!object} Deserialized TokenAccessResult value and its size
   */
  deserialize (value = new TokenAccessResult()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new TokenAccessResult(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new TokenAccessResult(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!TokenAccessResultModel}
   * @param {!number} prev Previous TokenAccessResult model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { TokenAccessResultModel };
